import { useState, useEffect, useRef, useCallback } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { InteractionOrder, Interaction, InteractionTag } from "../types/interactions";
import { getInteractionKey } from "../utils/interactionKey";

const isAbortError = (err: unknown): boolean => {
  if ((axios as any).isCancel?.(err)) return true;
  if ((err as any)?.code === "ERR_CANCELED") return true;
  if (typeof (err as any)?.message === "string" && (err as any).message.toLowerCase().includes("canceled")) return true;
  return (err instanceof Error && err.name === "CanceledError") || (err instanceof DOMException && err.name === "AbortError");
};

interface UseInteractionOrdersReturn {
  orders: InteractionOrder[];
  isLoading: boolean;
  error: string | null;
  existingInteractions: Record<string, any>;
  existingSkippedInteractions: Record<string, any>;
  initialInteractions: Record<string, Interaction>;
  taggedOrders: InteractionOrder[];
  tagOrderContacts: (orderNumber: string, contactIds: number[]) => Promise<void>;
  untagContact: (orderNumber: string, contactId: number) => Promise<void>;
  resolveContactTag: (orderNumber: string, contactId: number) => Promise<void>;
  abort: () => void;
}

export const useInteractionOrders = (
  selectedDate: dayjs.Dayjs | null,
  statusFilter: string,
  orderNumberSearch: string | null = null,
  linkedOrderNumbers: string[] | null = null
): UseInteractionOrdersReturn => {
  const [orders, setOrders] = useState<InteractionOrder[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [existingInteractions, setExistingInteractions] = useState<Record<string, any>>({});
  const [existingSkippedInteractions, setExistingSkippedInteractions] = useState<Record<string, any>>({});
  const [initialInteractions, setInitialInteractions] = useState<Record<string, Interaction>>({});
  const [taggedOrders, setTaggedOrders] = useState<InteractionOrder[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<string | null>(null);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsLoading(false);
    setError(null);
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const requestId = crypto.randomUUID();
    requestIdRef.current = requestId;

    const fetchInteractionOrders = async () => {
      if (!selectedDate && !orderNumberSearch && !linkedOrderNumbers?.length) {
        if (requestIdRef.current !== requestId) return;
        setIsLoading(false);
        setError(null);
        setOrders([]);
        setExistingInteractions({});
        setExistingSkippedInteractions({});
        setInitialInteractions({});
        setTaggedOrders([]);
        return;
      }

      if (requestIdRef.current !== requestId) return;
      setIsLoading(true);
      setError(null);
      setOrders([]);
      setExistingInteractions({});
      setExistingSkippedInteractions({});
      setInitialInteractions({});
      setTaggedOrders([]);

      try {
        let data: InteractionOrder[];

        if (orderNumberSearch) {
          const response = await axios.get<InteractionOrder[]>("/api/interactions/GetByOrderNumber", {
            params: { orderNumber: orderNumberSearch, status: statusFilter },
            signal: abortController.signal
          });
          data = response.data;
        } else if (linkedOrderNumbers?.length) {
          const response = await axios.post<InteractionOrder[]>(
            "/api/interactions/GetByOrderNumbers",
            { orderNumbers: linkedOrderNumbers, status: statusFilter },
            { signal: abortController.signal }
          );
          data = response.data;
        } else {
          const dateInt = parseInt(selectedDate!.format("YYYYMMDD"));
          const endpoint =
            statusFilter === "Closed" ? "/api/interactions/GetClosedOrders" : "/api/interactions/GetReceivedOrders";
          const response = await axios.get<InteractionOrder[]>(endpoint, {
            params: { date: dateInt },
            signal: abortController.signal
          });
          data = response.data;
        }

        let activeTagsData: InteractionTag[] = [];
        let taggedData: InteractionOrder[] = [];
        const tagMap: Record<string, InteractionTag> = {};

        if (statusFilter === "Closed") {
          try {
            const { data: tags } = await axios.get<InteractionTag[]>(
              "/api/clientphonebook/contactInteractionTags/active",
              {
                params: { interactionCategory: "Closed" },
                signal: abortController.signal
              }
            );
            activeTagsData = tags;
            activeTagsData.forEach((tag) => {
              if (tag.orderNumber && tag.contactId) {
                tagMap[getInteractionKey(tag.orderNumber, tag.contactId)] = tag;
              }
            });

            if (activeTagsData.length > 0) {
              const taggedOrderNumbers = [...new Set(activeTagsData.map((tag) => tag.orderNumber).filter(Boolean))];
              const { data: taggedOrderRows } = await axios.post<InteractionOrder[]>(
                "/api/interactions/GetByOrderNumbers",
                {
                  orderNumbers: taggedOrderNumbers,
                  status: "Closed"
                },
                { signal: abortController.signal }
              );
              taggedData = taggedOrderRows.filter((order) => {
                if (!order.orderNumber || !order.phonebookContactId) return false;
                const key = getInteractionKey(order.orderNumber, order.phonebookContactId);
                return Boolean(tagMap[key]);
              });
            }
          } catch (err) {
            if (isAbortError(err)) throw err;
          }
        }

        const allRowsForMetadata = [...data, ...taggedData];
        const orderContactTuples = allRowsForMetadata
          .filter((order) => order.orderNumber !== null && order.phonebookContactId !== null)
          .map((order) => ({
            orderNumber: order.orderNumber!,
            contactId: order.phonebookContactId!
          }));
        let existingInteractionsData: any[] = [];
        let existingSkippedInteractionsData: any[] = [];

        if (orderContactTuples.length > 0) {
          try {
            const { data: allInteractions } = await axios.post<any[]>(
              "/api/clientphonebook/contactInteractions/getByOrderNumbers",
              orderContactTuples,
              { signal: abortController.signal }
            );

            const isReceivedOnClosedTab = (i: any) =>
              statusFilter === "Closed" && i.interactionCategory === "Received";

            existingInteractionsData = allInteractions.filter(
              (i) => i.typeId !== 10 && !isReceivedOnClosedTab(i)
            );
            existingSkippedInteractionsData = allInteractions
              .filter((i) => i.typeId === 10 && !isReceivedOnClosedTab(i))
              .map((i) => ({
                id: i.id,
                orderNumber: i.orderNumber,
                contactId: i.contactId,
                skipReason: i.notes,
                skippedDate: i.interactionDate,
                skippedByUserId: i.clientCultivatorId,
                createdAt: i.createdAt,
                updatedAt: i.updatedAt
              }));
          } catch (err) {
            if (isAbortError(err)) throw err;
          }
        }

        const existingMap: Record<string, any> = {};
        existingInteractionsData.forEach((interaction) => {
          if (interaction.orderNumber && interaction.contactId) {
            const key = getInteractionKey(interaction.orderNumber, interaction.contactId);
            existingMap[key] = interaction;
          }
        });
        setExistingInteractions(existingMap);

        const existingSkippedMap: Record<string, any> = {};
        existingSkippedInteractionsData.forEach((skipped) => {
          if (skipped.orderNumber && skipped.contactId) {
            const key = getInteractionKey(skipped.orderNumber, skipped.contactId);
            existingSkippedMap[key] = skipped;
          }
        });
        setExistingSkippedInteractions(existingSkippedMap);

        let pastInteractionsCounts: Record<string, number> = {};

        if (orderContactTuples.length > 0) {
          try {
            const uniqueContactIds = [...new Set(orderContactTuples.map((t) => t.contactId))];
            const contactCountsMap = new Map<number, number>();

            const countPromises = uniqueContactIds.map(async (contactId) => {
              try {
                const { data: pastInteractions } = await axios.get(
                  "/api/clientphonebook/contactInteractions/getPastInteractions",
                  {
                    params: {
                      contactId
                    },
                    signal: abortController.signal
                  }
                );
                return { contactId, count: pastInteractions.length };
              } catch (err) {
                if (isAbortError(err)) throw err;
                return { contactId, count: 0 };
              }
            });

            const counts = await Promise.all(countPromises);
            counts.forEach(({ contactId, count }) => {
              contactCountsMap.set(contactId, count);
            });

            orderContactTuples.forEach((tuple) => {
              const key = getInteractionKey(tuple.orderNumber, tuple.contactId);
              pastInteractionsCounts[key] = contactCountsMap.get(tuple.contactId) || 0;
            });
          } catch (err) {
            if (isAbortError(err)) throw err;
            console.error("Error fetching past interactions counts:", err);
          }
        }

        const buildOrdersWithMetadata = (rows: InteractionOrder[]) => {
          const seenKeys = new Set<string>();
          return rows
          .map((order) => {
            const key = getInteractionKey(order.orderNumber!, order.phonebookContactId!);
            const existingInteraction = existingMap[key];
            const existingSkipped = existingSkippedMap[key];
            const pastInteractionsCount = pastInteractionsCounts[key] || 0;
            const tag = tagMap[key];
            const hasPendingTag = Boolean(tag) && !existingInteraction && !existingSkipped;

            const preInstructions =
              existingInteraction?.preInteractionInstructions || existingSkipped?.preInteractionInstructions || null;

            return {
              ...order,
              preInteractionInstructions: preInstructions,
              preInteractionInstructionsBy: preInstructions
                ? (existingInteraction?.clientCultivator || null)
                : null,
              preInteractionInstructionsAt: preInstructions
                ? (existingInteraction?.updatedAt || null)
                : null,
              pastInteractionsCount,
              isClosedConfirmed: false as boolean,
              closedConfirmedByUserId: null as number | null,
              closedConfirmedAt: null as string | null,
              closedConfirmedByUser: null as { id: number; firstName: string; lastName: string } | null,
              isTaggedForFollowUp: hasPendingTag,
              taggedAt: hasPendingTag ? tag?.taggedAt || null : null,
              taggedByUser: hasPendingTag ? tag?.taggedByUser || null : null
            };
          })
          .filter((order) => {
            const key = getInteractionKey(order.orderNumber!, order.phonebookContactId!);
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
          });
        };

        const hasPendingTag = (order: InteractionOrder) => {
          if (statusFilter !== "Closed" || !order.orderNumber || !order.phonebookContactId) return false;

          const key = getInteractionKey(order.orderNumber, order.phonebookContactId);
          return Boolean(tagMap[key]) && !existingMap[key] && !existingSkippedMap[key];
        };

        const inDateTaggedRows =
          statusFilter === "Closed"
            ? data.filter(hasPendingTag)
            : [];
        const regularRows =
          statusFilter === "Closed"
            ? data.filter((order) => {
                if (!order.orderNumber || !order.phonebookContactId) return true;
                return !hasPendingTag(order);
              })
            : data;

        const ordersWithInstructions = buildOrdersWithMetadata(regularRows);
        const taggedOrdersWithInstructions = buildOrdersWithMetadata([
          ...inDateTaggedRows,
          ...taggedData.filter(hasPendingTag)
        ]);
        const allOrdersWithInstructions = [...ordersWithInstructions, ...taggedOrdersWithInstructions];

        if (statusFilter === "Closed") {
          const uniqueOrderNumbers = [...new Set(allOrdersWithInstructions.map((o) => o.orderNumber).filter(Boolean))] as string[];
          if (uniqueOrderNumbers.length > 0) {
            try {
              const { data: confirmedOrders } = await axios.post<
                { orderNumber: string; confirmedByUserId: number; confirmedAt: string; confirmedByUser: { id: number; firstName: string; lastName: string } }[]
              >("/api/clientphonebook/confirmedClosedOrders/getByOrderNumbers", uniqueOrderNumbers, {
                signal: abortController.signal
              });
              const confirmedMap = new Map(confirmedOrders.map((c) => [c.orderNumber, c]));
              allOrdersWithInstructions.forEach((order) => {
                if (order.orderNumber) {
                  const confirmed = confirmedMap.get(order.orderNumber);
                  if (confirmed) {
                    order.isClosedConfirmed = true;
                    order.closedConfirmedByUserId = confirmed.confirmedByUserId;
                    order.closedConfirmedAt = confirmed.confirmedAt;
                    order.closedConfirmedByUser = confirmed.confirmedByUser;
                  }
                }
              });
            } catch (err) {
              if (isAbortError(err)) throw err;
            }
          }
        }

        if (abortController.signal.aborted || abortControllerRef.current?.signal.aborted || requestIdRef.current !== requestId) return;
        setOrders(ordersWithInstructions);
        setTaggedOrders(taggedOrdersWithInstructions);

        const initialInteractions: Record<string, Interaction> = {};
        existingInteractionsData.forEach((existing) => {
          if (existing.orderNumber && existing.contactId) {
            const key = getInteractionKey(existing.orderNumber, existing.contactId);
            initialInteractions[key] = {
              id: existing.id,
              orderNumber: existing.orderNumber,
              contactId: existing.contactId,
              propertyAddress: "",
              typeId: existing.typeId,
              type: existing.typeId ? existing.typeId.toString() : "",
              note: existing.notes || "",
              interactionDate: existing.interactionDate,
              clientCultivatorId: existing.clientCultivatorId
            };
          }
        });
        existingSkippedInteractionsData.forEach((skipped) => {
          if (skipped.orderNumber && skipped.contactId) {
            const key = getInteractionKey(skipped.orderNumber, skipped.contactId);
            initialInteractions[key] = {
                            orderNumber: skipped.orderNumber,
              contactId: skipped.contactId,
              propertyAddress: "",
              typeId: null,
              type: "",
              note: "",
              interactionDate: null,
              clientCultivatorId: skipped.skippedByUserId,
              skipped: true,
              skipReason: skipped.skipReason || ""
            };
          }
        });
        setInitialInteractions(initialInteractions);
      } catch (err) {
        if (isAbortError(err) || requestIdRef.current !== requestId) return;
        setError("Failed to load interaction orders");
        setOrders([]);
      } finally {
        if (!abortController.signal.aborted && requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    };

    fetchInteractionOrders();

    return () => {
      abortController.abort();
    };
  }, [selectedDate, statusFilter, orderNumberSearch, linkedOrderNumbers]);

  const tagOrderContacts = useCallback(async (orderNumber: string, contactIds: number[]) => {
    const normalizedContactIds = [...new Set(contactIds.filter((id) => id > 0))];
    if (!orderNumber || normalizedContactIds.length === 0) return;

    const { data: createdTags } = await axios.post<InteractionTag[]>("/api/clientphonebook/contactInteractionTags/tagOrder", {
      orderNumber,
      contactIds: normalizedContactIds,
      interactionCategory: "Closed"
    });

    const tagByContactId = new Map(createdTags.map((tag) => [tag.contactId, tag]));
    const applyTagState = (order: InteractionOrder) => {
      if (order.orderNumber !== orderNumber || !order.phonebookContactId) return order;

      const tag = tagByContactId.get(order.phonebookContactId);
      if (!tag) return order;

      return {
        ...order,
        isTaggedForFollowUp: true,
        taggedAt: tag.taggedAt,
        taggedByUser: tag.taggedByUser
      };
    };

    setOrders((prev) => prev.map(applyTagState));
    setTaggedOrders((prev) => prev.map(applyTagState));
  }, []);

  const untagContact = useCallback(async (orderNumber: string, contactId: number) => {
    if (!orderNumber || !contactId) return;

    try {
      await axios.delete(
        `/api/clientphonebook/contactInteractionTags/contact/${encodeURIComponent(orderNumber)}/${contactId}`,
        { params: { interactionCategory: "Closed" } }
      );
    } catch (err) {
      if ((axios as any).isAxiosError?.(err) && (err as any).response?.status !== 404) throw err;
    }

    setOrders((prev) =>
      prev.map((order) =>
        order.orderNumber === orderNumber && order.phonebookContactId === contactId
          ? { ...order, isTaggedForFollowUp: false, taggedAt: null, taggedByUser: null }
          : order
      )
    );
    setTaggedOrders((prev) =>
      prev.map((order) =>
        order.orderNumber === orderNumber && order.phonebookContactId === contactId
          ? { ...order, isTaggedForFollowUp: false, taggedAt: null, taggedByUser: null }
          : order
      )
    );
  }, []);

  const resolveContactTag = useCallback(async (orderNumber: string, contactId: number) => {
    if (!orderNumber || !contactId) return;

    setOrders((prev) =>
      prev.map((order) =>
        order.orderNumber === orderNumber && order.phonebookContactId === contactId
          ? { ...order, isTaggedForFollowUp: false, taggedAt: null, taggedByUser: null }
          : order
      )
    );
    setTaggedOrders((prev) =>
      prev.map((order) =>
        order.orderNumber === orderNumber && order.phonebookContactId === contactId
          ? { ...order, isTaggedForFollowUp: false, taggedAt: null, taggedByUser: null }
          : order
      )
    );
  }, []);

  return {
    orders,
    isLoading,
    error,
    existingInteractions,
    existingSkippedInteractions,
    initialInteractions,
    taggedOrders,
    tagOrderContacts,
    untagContact,
    resolveContactTag,
    abort
  };
};
